import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Check, X, FileText, Filter } from "lucide-react";
import Navbar from "@/components/Navbar";
import { StatusBadge, GolonganBadge } from "@/components/Badges";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerificationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAct = (st) => st === "Pending" || (isAdmin && st === "Validated");
  const [meta, setMeta] = useState({ golongan: [], kwarran: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", kwarran: "", golongan: "", q: "" });
  const [modal, setModal] = useState(null); // {ach, action}
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/achievements", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setBusy(true);
    try {
      await api.patch(`/achievements/${modal.ach.id}/verify`, { status: modal.action, feedback: feedback || null });
      toast.success(modal.action === "Approved" ? (isAdmin ? "Prestasi disetujui" : "Prestasi divalidasi, diteruskan ke Kwarcab") : "Prestasi ditolak");
      setModal(null); setFeedback("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const selectCls = "rounded-lg border border-[#E5DFD7] bg-white px-3 py-2 text-sm outline-none focus:border-[#E5A93C]";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-extrabold text-[#1F1917] flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-[#B85C38]" /> Verifikasi Prestasi
          </h1>
          <p className="text-[#8C827A] mt-1" data-testid="verification-role-hint">
            {isAdmin
              ? "Verifikasi akhir Kwarcab: setujui prestasi yang sudah divalidasi Pembina (atau langsung dari anggota)."
              : "Validasi awal Pembina: periksa prestasi anggota gudep Anda sebelum diteruskan ke Kwarcab."}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-[#E5DFD7] p-4 mb-6 flex flex-wrap gap-3 items-center">
          <span className="text-xs font-bold uppercase tracking-widest text-[#8C827A] flex items-center gap-1"><Filter className="h-3.5 w-3.5" />Filter</span>
          <input data-testid="verification-search" className={selectCls + " flex-1 min-w-[180px]"} placeholder="Cari nama / NTA / judul"
            value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
          <select data-testid="verification-filter-status" className={selectCls} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Semua Status</option>
            <option value="Pending">Menunggu Pembina</option>
            <option value="Validated">Menunggu Kwarcab</option>
            <option value="Approved">Terverifikasi</option>
            <option value="Rejected">Ditolak</option>
          </select>
          <select data-testid="admin-filter-golongan" className={selectCls} value={filters.golongan} onChange={(e) => setFilters((f) => ({ ...f, golongan: e.target.value }))}>
            <option value="">Semua Golongan</option>
            {meta.golongan.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select data-testid="admin-filter-kwarran" className={selectCls} value={filters.kwarran} onChange={(e) => setFilters((f) => ({ ...f, kwarran: e.target.value }))}>
            <option value="">Semua Kwarran</option>
            {meta.kwarran.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#B85C38]" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-[#E5DFD7] p-16 text-center text-[#8C827A]">
            Tidak ada prestasi yang cocok dengan filter.
          </div>
        ) : (
          <div className="grid gap-4" data-testid="verification-list">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-semibold text-[#1F1917]">{it.judul}</h3>
                      <StatusBadge value={it.status} />
                      <GolonganBadge value={it.golongan} />
                    </div>
                    <div className="text-sm text-[#4A423F] mt-1">{it.tingkat} · {it.kategori} · {it.tahun}</div>
                    <div className="text-xs text-[#8C827A] mt-1">
                      {it.nama} · NTA {it.nta} · {it.gudep || "-"} · {it.kwarran || "-"}
                    </div>
                    {it.sertifikat_path && (
                      <a href={`${api.defaults.baseURL}/files/${it.sertifikat_path}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#B85C38] hover:underline">
                        <FileText className="h-3.5 w-3.5" /> Lihat Sertifikat
                      </a>
                    )}
                    {it.pembina_verified_by && <div className="text-xs text-sky-800 mt-1">Divalidasi Pembina: {it.pembina_verified_by}</div>}
                    {it.verified_by && <div className="text-xs text-[#8C827A] mt-1">Diverifikasi oleh: {it.verified_by}</div>}
                  </div>
                  {canAct(it.status) && (
                    <div className="flex gap-2">
                      <button data-testid="verification-approve-btn" onClick={() => { setModal({ ach: it, action: "Approved" }); setFeedback(""); }}
                        className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-emerald-700 transition">
                        <Check className="h-4 w-4" /> {isAdmin ? "Setujui" : "Validasi"}
                      </button>
                      <button data-testid="verification-reject-btn" onClick={() => { setModal({ ach: it, action: "Rejected" }); setFeedback(""); }}
                        className="inline-flex items-center gap-1.5 bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-rose-700 transition">
                        <X className="h-4 w-4" /> Tolak
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div data-testid="verification-modal-feedback" className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-xl font-extrabold text-[#1F1917] mb-1">
              {modal.action === "Approved" ? "Setujui Prestasi" : "Tolak Prestasi"}
            </h2>
            <p className="text-sm text-[#8C827A] mb-4">{modal.ach.judul} — {modal.ach.nama}</p>
            <textarea className="w-full rounded-lg border border-[#E5DFD7] px-3.5 py-2.5 text-sm outline-none focus:border-[#E5A93C] focus:ring-2 focus:ring-[#E5A93C]/30 min-h-[90px]"
              placeholder={modal.action === "Approved" ? "Catatan (opsional)" : "Alasan penolakan (opsional)"}
              value={feedback} onChange={(e) => setFeedback(e.target.value)} />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-lg border border-[#E5DFD7] font-bold text-[#4A423F] hover:bg-[#F7F4EF] transition">Batal</button>
              <button data-testid="verification-confirm-btn" onClick={verify} disabled={busy}
                className={`flex-1 py-2.5 rounded-lg text-white font-bold transition disabled:opacity-60 flex items-center justify-center gap-2 ${modal.action === "Approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Konfirmasi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
